import React, { useEffect, useRef, useState } from 'react';
import { ifProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';
import ModularBlocks from 'components/ModularBlocks';
import pageBodyShape from 'shapes/pageBodyShape';
import BaseBox from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import BaseAccordion from '@mui/material/Accordion';
import BaseAccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { useAuth } from 'context/AuthContext';
import Icon from 'components/Icon';

const Box = styled(BaseBox)`
  background-color: ${theme('colors.black')};
  border-top: 1px solid #212626;
  box-shadow: inset 0 0.0625rem 0.1875rem 0 rgba(0, 0, 0, 0.3);
  padding-top: 1rem;
  position: relative;

  ${props => props.theme.breakpoints.down('md')} {
    position: relative;
    z-index: 11;
  }

  ${ifProp('$loggedIn', css`
    padding-bottom: 2.5rem;
  `)}
`;

const DisclaimerContainer = styled(Container)`
  ${props => props.theme.breakpoints.up('md')} {
    width: 88%;
    padding-left: 0;
    padding-right: 0;
  }

  ${props => props.theme.breakpoints.up('lg')} {
    max-width: 1280px;
  }
  
  && {
    @media (min-width: 1440px) {
      max-width: 1380px;
    }
  
    ${props => props.theme.breakpoints.up('xl')} {
      max-width: 1580px;
    }
  }

  a,
  p {
    font-size: 0.75rem;
    padding-bottom: 0;
    color: ${theme('colors.white')};
    font-family: Heebo;
    letter-spacing: normal;
    font-stretch: normal;
  }

  a {
    &:hover {
      color: ${theme('colors.white')};
    }
  }

  .modular-block-disclaimerBody {
    max-width: inherit;
    padding: 0;
  }

  .one-trust-icon {
    width: 27px;
    height: 13px;
    max-width: none;
    display: inline-block;
    vertical-align: middle;
    margin-left: 3px;
    cursor: pointer;
  }

  .one-trust-text {
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
`;

const Accordion = styled(BaseAccordion)`
  border-radius: 0;
  background-color: ${theme('colors.black')};
  display: flex;
  flex-direction: column;

  ${props => props.theme.breakpoints.up('sm')} {
    flex-direction: column-reverse;
  }

  .MuiAccordionDetails-root {
    padding: 0;

    .MuiBox-root {
      &:last-child {
        ${props => props.theme.breakpoints.up('sm')} {
          margin-bottom: 0;
        }
      }
    }
  }

  .MuiAccordionSummary-expandIcon {
    margin-top: -0.5rem;
    font-size: 1.3rem;

    ${props => props.theme.breakpoints.up('sm')} {
      margin-top: -1rem;
    }
  }
`;

const AccordionSummary = styled(BaseAccordionSummary)`
  ${props => props.theme.breakpoints.down('sm')} {
    flex-direction: column-reverse;
  }

  .MuiAccordionSummary-content {
    color: ${theme('colors.white')};
    font-size: 0.8125rem;
    font-weight: 300;
    margin: 1.25rem 0;

    ${props => props.theme.breakpoints.down('sm')} {
      flex-grow: 0;
    }
  }

  // since the icon and text are being flipped by column-reverse, it is not
  // possible to use 'display: none' or 'visibility: hidden' to hide this text
  // when the accordion expands
  ${ifProp('$showCopyright', css`
    .MuiAccordionSummary-content {
      ${props => props.theme.breakpoints.down('sm')} {
        height: 0;
        margin: 0;
        overflow: hidden;
      }
    }
  `)}
`;

function Disclaimer({ disclaimers }) {
  const [expand, setExpand] = useState(false);
  const theme = useTheme();
  const { user } = useAuth();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const year = new Date().getFullYear();
  const preventDoubleClick = useRef(false);
  const isBrowser = typeof window !== 'undefined';

  useEffect(() => {
    isDesktop && setExpand(true);
  }, [isDesktop]);

  useEffect(() => {
    const triggerOneTrust = document.querySelectorAll('.one-trust-icon, .one-trust-trigger');
    triggerOneTrust.forEach((icon) => {
      icon.tabIndex = 0;
    });
  }, [disclaimers]);

  const handleChange = () => (_event, open) => {
    setExpand(!!open);
  };

  const openOneTrustPreferences = () => {
    if (!isBrowser) {
      return;
    }

    if (window.OneTrust?.ToggleInfoDisplay) {
      window.OneTrust.ToggleInfoDisplay();
    } else if (window.Optanon?.ToggleInfoDisplay) {
      window.Optanon.ToggleInfoDisplay();
    }
  };

  const handleOneTrustIconActivate = (e) => {
    const triggerOneTrust = e.target.closest?.('.one-trust-icon, .one-trust-trigger');
    if (!triggerOneTrust) {
      return false;
    }

    if (e.type === 'click' && preventDoubleClick.current) {
      preventDoubleClick.current = false;
      return false;
    }

    if (e.type === 'keydown') {
      const isActivateKey = e.key === 'Enter' || e.key === ' ';
      if (!isActivateKey) {
        return false;
      }

      e.preventDefault();
      preventDoubleClick.current = true;
    }

    e.stopPropagation();
    openOneTrustPreferences();
  };

  return (
    <Box $loggedIn={!!user}>
      <DisclaimerContainer
        onClickCapture={handleOneTrustIconActivate}
        onKeyDownCapture={handleOneTrustIconActivate}
      >
        <Accordion expanded={expand} onChange={handleChange()}>
          <AccordionSummary
            $showCopyright={expand}
            expandIcon={
              <Icon
                color={theme.palette.common.white}
                icon='chevronDown'
                size={1}
              />
            }
          >
          {!expand && <span>{`© copyright 2013 - ${year} tastylive, Inc.`}</span>}
          </AccordionSummary>
          <AccordionDetails>
            <ModularBlocks modularBlocks={disclaimers} />
          </AccordionDetails>
        </Accordion>
      </DisclaimerContainer>
    </Box>
  );
}

Disclaimer.propTypes = {
  disclaimers: pageBodyShape.isRequired
};

export default Disclaimer;
